import { Check, RefreshCw, FileText, GraduationCap, Wrench, Hourglass, Clock, Percent, PlusCircle, Info, Download } from "lucide-react";
import { EXT_COLS, EXT_ROWS, MOD_COLS, MOD_ROWS } from "./data";

const SCROLL_MARGIN = { scrollMarginTop: "240px" };

function ExperienceTable({ t }) {
  const fmtCell = (v) => {
    if (v === "—") return v;
    if (v === "None") return t("coursesPage.rules.none");
    return v.replace("m", t("coursesPage.rules.m")).replace("y", t("coursesPage.rules.y"));
  };

  return (
    <div id="rules-time" style={SCROLL_MARGIN} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="extension-table">
          <thead>
            <tr className="bg-[#0A1628] text-white">
              <th className="px-3 py-3 text-left font-bold text-[11px] uppercase tracking-wider whitespace-nowrap sticky left-0 bg-[#0A1628] z-10">
                {t("coursesPage.rules.fromTo")}
              </th>
              {EXT_COLS.map((c) => (
                <th key={c} className="px-3 py-3 text-center font-bold text-[12px] uppercase tracking-wider whitespace-nowrap min-w-[64px]">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EXT_ROWS.map((row, ri) => (
              <tr key={row.from} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="px-3 py-2.5 font-bold text-[#0A1628] text-[13px] whitespace-nowrap sticky left-0 bg-inherit border-r border-slate-200">
                  {row.from}
                </td>
                {row.cells.map((cell, ci) => {
                  const isDiag = cell === "—";
                  const isNone = cell === "None";
                  return (
                    <td
                      key={ci}
                      className={`px-3 py-2.5 text-center text-[13px] whitespace-nowrap ${
                        isDiag ? "text-slate-300" : isNone ? "text-emerald-600 font-bold" : "text-slate-700 font-semibold"
                      }`}
                    >
                      {fmtCell(cell)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModulesTable({ t }) {
  const isPT = t("coursesPage.rules.moduleNone") === "Nenhum";
  const isES = t("coursesPage.rules.moduleNone") === "Ninguno";
  const compact = (s) => s.replace(/, /g, "·");

  return (
    <div className="mt-12" id="rules-mods" style={SCROLL_MARGIN}>
      <div className="bg-gradient-to-br from-[#F59E0B]/5 to-[#10B981]/5 border border-[#F59E0B]/20 rounded-xl p-4 md:p-5 mb-6">
        <p className="text-sm font-bold text-[#0A1628] mb-1.5">📚 {t("coursesPage.rules.modulesSimple")}</p>
      </div>
      <h3 className="font-['Outfit'] text-xl md:text-2xl font-extrabold tracking-tight text-[#0A1628] mb-3">
        {t("coursesPage.rules.modulesTitle")}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-6 max-w-4xl">{t("coursesPage.rules.modulesIntro")}</p>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse table-fixed min-w-[640px]" data-testid="modules-conversion-table">
          <thead>
            <tr className="bg-[#0A1628] text-white">
              <th className="px-1.5 py-3 text-center font-bold text-[11px] uppercase tracking-wider w-[68px]">
                {t("coursesPage.rules.fromTo")}
              </th>
              {MOD_COLS.map((c) => (
                <th key={c} className="px-1 py-3 text-center font-bold text-[11px] uppercase tracking-wider">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MOD_ROWS.map((row, ri) => (
              <tr key={row.from} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="px-1.5 py-2.5 font-bold text-[#0A1628] text-[12px] text-center border-r border-slate-200">{row.from}</td>
                {row.cells.map((cell, ci) => {
                  const isDiag = cell === "—";
                  const isNone = cell === "None";
                  const isAll = cell === "ALL_EXC_9";
                  return (
                    <td key={ci} className="px-1 py-2.5 text-center whitespace-nowrap">
                      {isDiag ? (
                        <span className="text-slate-300 text-base">—</span>
                      ) : isNone ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600">
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        </span>
                      ) : isAll ? (
                        <span className="inline-block px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded font-bold text-[11px]">All*</span>
                      ) : (
                        <span className="text-slate-700 font-semibold text-[11px]">{compact(cell)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-600">
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
          = {isPT ? "não precisas de fazer módulos extra" : isES ? "no necesitas hacer módulos extra" : "no extra modules needed"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded font-bold text-[11px]">All*</span>
          = {isPT ? "todos os módulos exceto Fatores Humanos (M9)" : isES ? "todos los módulos excepto Factores Humanos (M9)" : "all modules except Human Factors (M9)"}
        </span>
        <span className="text-slate-500">
          {isPT ? "Os números indicam os módulos a estudar (M11, M15, etc.)." : isES ? "Los números indican los módulos a estudiar (M11, M15, etc.)." : "Numbers indicate modules to study (M11, M15, etc.)."}
        </span>
      </div>
    </div>
  );
}

function BasicExperienceBlock({ t }) {
  const groups = [
    {
      key: "G1",
      color: "#00A8FF",
      gradient: "from-[#00A8FF]/5 to-[#00A8FF]/0",
      cats: ["A", "B1.2", "B1.4", "B3"],
      title: t("coursesPage.basicExp.group1Title"),
      hint: t("coursesPage.basicExp.group1Hint"),
      paths: [
        { years: 3, label: t("coursesPage.basicExp.pathNoTraining"), tag: "Ø" },
        { years: 2, label: t("coursesPage.basicExp.pathSkilled"), tag: "Skilled" },
        { years: 1, label: t("coursesPage.basicExp.pathPart147"), tag: "Part-147", accent: true },
      ],
    },
    {
      key: "G2",
      color: "#F59E0B",
      gradient: "from-[#F59E0B]/5 to-[#F59E0B]/0",
      cats: ["B2", "B1.1", "B1.3"],
      title: t("coursesPage.basicExp.group2Title"),
      hint: t("coursesPage.basicExp.group2Hint"),
      paths: [
        { years: 5, label: t("coursesPage.basicExp.pathNoTraining"), tag: "Ø" },
        { years: 3, label: t("coursesPage.basicExp.pathSkilled"), tag: "Skilled" },
        { years: 2, label: t("coursesPage.basicExp.pathPart147"), tag: "Part-147", accent: true },
      ],
    },
  ];

  return (
    <div className="mb-12" data-testid="basic-experience-block">
      <div className="bg-gradient-to-br from-[#0A1628] to-[#1A2D4A] rounded-2xl px-6 py-7 md:px-9 md:py-8 mb-6 border border-slate-700/50 shadow-lg">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="inline-flex items-center gap-1.5 bg-[#00A8FF]/15 text-[#00A8FF] text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded">
            EASA Part-66.A.30
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            {t("coursesPage.basicExp.source")}
          </span>
        </div>
        <h3 className="font-['Outfit'] text-2xl md:text-3xl font-extrabold text-white mb-2 leading-tight">
          {t("coursesPage.basicExp.title")}
        </h3>
        <p className="text-sm md:text-[15px] text-slate-300 leading-relaxed max-w-3xl">
          {t("coursesPage.basicExp.intro")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {groups.map((g) => (
          <div
            key={g.key}
            className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden`}
            data-testid={`basic-exp-${g.key}`}
          >
            {/* Card header */}
            <div className={`bg-gradient-to-br ${g.gradient} px-6 py-5 border-b border-slate-100`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {g.cats.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center justify-center px-2.5 py-1 rounded-md font-['Outfit'] font-extrabold text-[12px] tracking-tight"
                    style={{ backgroundColor: `${g.color}15`, color: g.color, border: `1px solid ${g.color}30` }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              <h4 className="font-['Outfit'] font-extrabold text-[15px] text-[#0A1628] leading-tight mb-0.5">
                {g.title}
              </h4>
              <p className="text-[12px] text-slate-500">{g.hint}</p>
            </div>

            {/* Paths */}
            <div className="px-6 py-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-4">
                {t("coursesPage.basicExp.threePaths")}
              </p>
              <ul className="space-y-3">
                {g.paths.map((p, i) => (
                  <li key={i} className="relative">
                    {i > 0 && (
                      <div className="absolute -top-2.5 left-[34px] -translate-x-1/2 text-[9px] font-bold tracking-widest text-slate-300 bg-white px-1">
                        {t("coursesPage.basicExp.altLabel")}
                      </div>
                    )}
                    <div
                      className={`flex items-stretch gap-3 rounded-xl border ${
                        p.accent ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"
                      } p-3`}
                    >
                      {/* Year badge */}
                      <div
                        className="shrink-0 w-[68px] rounded-lg flex flex-col items-center justify-center py-2 font-['Outfit']"
                        style={{
                          backgroundColor: p.accent ? "#10B98115" : `${g.color}10`,
                          border: `1px solid ${p.accent ? "#10B98130" : `${g.color}25`}`,
                          color: p.accent ? "#059669" : g.color,
                        }}
                      >
                        <span className="text-2xl font-black leading-none">{p.years}</span>
                        <span className="text-[9px] font-bold uppercase tracking-wider mt-0.5 opacity-80">
                          {p.years === 1 ? t("coursesPage.basicExp.yearLabel") : t("coursesPage.basicExp.yearsLabel")}
                        </span>
                      </div>

                      {/* Description */}
                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span
                            className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              p.accent ? "bg-emerald-100 text-emerald-700" : ""
                            }`}
                            style={!p.accent ? { backgroundColor: `${g.color}15`, color: g.color } : {}}
                          >
                            {p.tag}
                          </span>
                        </div>
                        <p className="text-[13px] text-slate-700 leading-snug font-medium">
                          {p.label}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Footer note */}
            <div className="bg-slate-50 px-6 py-3 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                <span className="font-bold text-[#0A1628]">{t("coursesPage.basicExp.requirementLabel")}:</span>{" "}
                {t("coursesPage.basicExp.requirement")}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PracticalSection({ t }) {
  const cases = [
    { key: "A",  color: "#00A8FF", title: t("coursesPage.rules.caseATitle"), lines: [
      { label: "1 year · 200 days", icon: GraduationCap, text: t("coursesPage.rules.caseAReq") },
      { icon: Clock, text: t("coursesPage.rules.caseARecent") },
      { label: "Skilled · 2 years · 400 days", icon: Wrench, text: t("coursesPage.rules.caseAPathSkilled") },
      { label: "No training · 3 years · 600 days", icon: Hourglass, text: t("coursesPage.rules.caseAPathNoTraining") },
    ]},
    { key: "B1", color: "#F59E0B", title: t("coursesPage.rules.caseB1Title"), lines: [
      { label: "2 years · 400 days", icon: GraduationCap, text: t("coursesPage.rules.caseB1Req") },
      { icon: Clock, text: t("coursesPage.rules.caseB1Recent") },
      { label: "Skilled · 3 years · 600 days", icon: Wrench, text: t("coursesPage.rules.caseB1PathSkilled") },
      { label: "No training · 5 years · 1000 days", icon: Hourglass, text: t("coursesPage.rules.caseB1PathNoTraining") },
    ]},
    { key: "EXT", color: "#10B981", title: t("coursesPage.rules.caseExtTitle"), lines: [
      { icon: Info, text: t("coursesPage.rules.caseExtIntro") },
      { label: "−50% Ab-Initius", icon: Percent, text: t("coursesPage.rules.caseExtAb") },
      { icon: Clock, text: t("coursesPage.rules.caseExtRecent") },
      { label: "+ A1 automático", icon: PlusCircle, text: t("coursesPage.rules.caseExtAuto"), accent: true },
    ]},
  ];

  return (
    <div id="step" style={SCROLL_MARGIN} data-testid="track-step">
      <div className="text-center mb-10">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2">🚀 Step-by-step</p>
        <h2 className="font-['Outfit'] text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1628] mb-3">
          {t("coursesPage.rules.practicalTitle")}
        </h2>
        <p className="text-base text-slate-500 max-w-2xl mx-auto">{t("coursesPage.rules.practicalIntro")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {cases.map((c) => (
          <div key={c.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col" data-testid={`case-${c.key}`}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-['Outfit'] font-extrabold text-sm"
                style={{ backgroundColor: `${c.color}15`, border: `1px solid ${c.color}30`, color: c.color }}
              >
                {c.key}
              </div>
              <h4 className="font-['Outfit'] font-extrabold text-[15px] text-[#0A1628] leading-tight">{c.title}</h4>
            </div>
            <ul className="space-y-3 text-[13px] text-slate-700 leading-relaxed">
              {c.lines.map((line, i) => {
                const Icon = line.icon;
                return (
                  <li key={i} className="flex gap-2.5">
                    {Icon ? (
                      <span
                        className="mt-0.5 w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: line.accent ? "#10B98115" : `${c.color}15`,
                          color: line.accent ? "#059669" : c.color,
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                      </span>
                    ) : (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                    )}
                    <div className="min-w-0 flex-1">
                      {line.label && (
                        <span
                          className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1 ${line.accent ? "bg-emerald-100 text-emerald-700" : ""}`}
                          style={!line.accent ? { backgroundColor: `${c.color}15`, color: c.color } : {}}
                        >
                          {line.label}
                        </span>
                      )}
                      <p>{line.text}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Basic Experience Requirements (EASA Part-66.A.30) — placed after the case studies */}
      <div className="mt-12">
        <BasicExperienceBlock t={t} />
      </div>
    </div>
  );
}

function DocsSection({ t, lang }) {
  const docsLog = [
    t("coursesPage.rules.docsLog1"), t("coursesPage.rules.docsLog2"), t("coursesPage.rules.docsLog3"),
    t("coursesPage.rules.docsLog4"), t("coursesPage.rules.docsLog5"), t("coursesPage.rules.docsLog6"),
    t("coursesPage.rules.docsLog7"),
  ];
  const docs = [t("coursesPage.rules.doc1"), t("coursesPage.rules.doc2"), t("coursesPage.rules.doc3")];

  // PDF only available for PT for now; user will provide the other languages later
  const formPdf = lang === "pt" ? "/forms/Form19_PT.pdf" : null;

  return (
    <div id="rules-docs" style={SCROLL_MARGIN} className="mt-10 bg-gradient-to-br from-[#0A1628] to-[#1A2D4A] rounded-2xl p-6 md:p-8 border border-slate-700/50 shadow-lg">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center shrink-0">
          <FileText className="w-6 h-6 text-[#F59E0B]" />
        </div>
        <div>
          <h4 className="font-['Outfit'] font-extrabold text-xl text-white mb-1">{t("coursesPage.rules.docsTitle")}</h4>
          <p className="text-sm text-slate-400">{t("coursesPage.rules.docsIntro")}</p>
        </div>
      </div>

      <ol className="space-y-2.5 mb-7 list-none">
        {docs.map((d, i) => (
          <li key={i} className="flex flex-wrap items-start gap-3 text-[14px] text-slate-200 leading-relaxed">
            <span className="w-6 h-6 rounded-full bg-[#00A8FF]/15 text-[#00A8FF] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
            {i === 0 && formPdf && (
              <a
                href={formPdf}
                download="EASA_Form19_v5.pdf"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download EASA Form 19 v5 (PDF)"
                className="form19-pulse group relative inline-flex items-center gap-2.5 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-[length:200%_100%] hover:bg-[position:100%_0] text-[#0A1628] font-extrabold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-lg hover:shadow-[0_8px_24px_-4px_rgba(245,158,11,0.55)] hover:-translate-y-0.5 transition-transform duration-300 shrink-0 order-2 sm:order-none"
                data-testid="download-form19-btn"
              >
                <span className="form19-ring absolute inset-0 rounded-lg ring-2 ring-[#F59E0B]/40 group-hover:ring-[#F59E0B]/0 pointer-events-none" />
                <Download className="w-3.5 h-3.5 group-hover:animate-bounce" strokeWidth={2.6} />
                <span className="whitespace-nowrap">Download now <span className="font-black tracking-wider">FORM&nbsp;19</span></span>
              </a>
            )}
            <span className="flex-1 min-w-[200px] basis-full sm:basis-0">{d}</span>
          </li>
        ))}
      </ol>

      <p className="text-[13px] font-bold uppercase tracking-wider text-[#00A8FF] mb-3">
        {t("coursesPage.rules.docsLogTitle")}
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {docsLog.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-slate-300">
            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" strokeWidth={3} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RulesSection({ t, lang }) {
  return (
    <div id="rules" style={SCROLL_MARGIN} className="mt-20" data-testid="track-rules">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 bg-[#00A8FF]/15 border border-[#00A8FF]/30">
          <RefreshCw className="w-7 h-7 text-[#00A8FF]" />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">EASA Part 66 — Appendix III</p>
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-extrabold tracking-tight text-[#0A1628]">
            {t("coursesPage.rules.title")}
          </h2>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#00A8FF]/5 to-[#F59E0B]/5 border border-[#00A8FF]/20 rounded-xl p-4 md:p-5 mb-6">
        <p className="text-sm font-bold text-[#0A1628] mb-1">{t("coursesPage.rules.focus")}</p>
        <p className="text-xs text-slate-600">{t("coursesPage.rules.followup")}</p>
      </div>

      <div className="space-y-3 mb-6 text-sm text-slate-600 leading-relaxed">
        <p>{t("coursesPage.rules.intro")}</p>
        <p><span className="text-[#0A1628] font-semibold">•</span> {t("coursesPage.rules.line1")}</p>
        <p><span className="text-[#0A1628] font-semibold">•</span> {t("coursesPage.rules.line2")}</p>
      </div>

      <ExperienceTable t={t} />

      <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-slate-500">
        <span><span className="font-bold text-[#0A1628]">m</span> = {t("coursesPage.rules.m") === "m" ? "months" : "meses"}</span>
        <span><span className="font-bold text-[#0A1628]">y</span> = {t("coursesPage.rules.y") === "y" ? "years" : "anos"}</span>
        <span className="text-emerald-600 font-bold">{t("coursesPage.rules.none")}</span>
        <span>= {t("coursesPage.rules.none") === "None" ? "no extra experience required" : "sem experiência extra"}</span>
      </div>

      {/* Days-per-month notice */}
      <div className="mt-5 flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 font-bold text-sm">!</div>
        <div>
          <p className="text-sm font-bold text-[#0A1628] mb-1">{t("coursesPage.rules.daysNote")}</p>
          <p className="text-[13px] text-slate-700 leading-relaxed">{t("coursesPage.rules.daysNoteDesc")}</p>
        </div>
      </div>

      <ModulesTable t={t} />
      <DocsSection t={t} lang={lang} />
    </div>
  );
}
